import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, UserPlus, X, Loader2, Copy, Crown, Lock } from "lucide-react";
import { Link } from "react-router-dom";

export default function TeamPanel() {
  const { user } = useAuth();
  const isEnterprise = user?.subscription_tier === "enterprise";
  const [team, setTeam] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);

  const load = () => {
    if (!isEnterprise) return;
    api.get("/teams").then((r) => setTeam(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [isEnterprise]);

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter an email"); return; }
    setInviting(true);
    try {
      const res = await api.post("/teams/invite", { email: inviteEmail.trim() });
      toast.success(`Invite sent to ${inviteEmail}`);
      const link = `${window.location.origin}/accept-invite?token=${res.data.invite_token}`;
      setLastInviteLink(link);
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const removeMember = async (userId) => {
    if (!confirm("Remove this member from the team?")) return;
    await api.delete(`/teams/members/${userId}`);
    toast.success("Member removed");
    load();
  };

  const revokeInvite = async (inviteId) => {
    await api.delete(`/teams/invites/${inviteId}`);
    toast.success("Invite revoked");
    load();
  };

  if (!isEnterprise) {
    return (
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8" data-testid="team-panel-locked">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-[#5E5E5E] mt-1"/>
          <div>
            <h2 className="font-heading text-xl font-medium">Team seats</h2>
            <p className="text-sm text-[#5E5E5E] mt-1 max-w-md">
              Enterprise plan includes 5 seats. Invite teammates to collaborate on applications and share documents.
            </p>
          </div>
        </div>
        <div className="mt-6 rounded-md bg-[#F9F8F6] border border-dashed border-[#E6E4DF] p-6 text-center">
          <Lock className="w-6 h-6 text-[#5E5E5E] mx-auto"/>
          <p className="text-sm text-[#1A1A1A] mt-3">Available on the Enterprise plan.</p>
          <Link to="/pricing">
            <Button data-testid="team-upgrade-cta" className="mt-4 bg-[#1C3F35] text-white hover:bg-[#1A362D]">
              See Enterprise
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8 text-[#5E5E5E]">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading team…
      </div>
    );
  }

  const seatsUsed = (team.members?.length || 0) + (team.pending_invites?.length || 0);

  return (
    <div className="bg-white border border-[#E6E4DF] rounded-lg p-8" data-testid="team-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-[#1C3F35] mt-1"/>
          <div>
            <h2 className="font-heading text-xl font-medium">{team.team?.name || "Team"}</h2>
            <p className="text-sm text-[#5E5E5E] mt-1">
              {seatsUsed} of {team.max_seats} seats used. Applications & documents are shared across the team.
            </p>
          </div>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button
              disabled={seatsUsed >= team.max_seats}
              data-testid="team-invite-open"
              className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
            >
              <UserPlus className="w-4 h-4 mr-2"/>Invite
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md" data-testid="team-invite-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Invite teammate</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email" value={inviteEmail}
                  onChange={(e)=>setInviteEmail(e.target.value)}
                  data-testid="team-invite-email"
                  className="mt-2 bg-white border-[#E6E4DF]"
                  placeholder="teammate@nonprofit.org"
                />
              </div>
              {lastInviteLink && (
                <div className="rounded-md bg-[#F9F8F6] border border-[#E6E4DF] p-3">
                  <div className="text-xs text-[#7A8C77] uppercase tracking-widest font-bold mb-1">Share link</div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-mono text-[#1A1A1A] truncate flex-1">{lastInviteLink}</div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Copied"); }}
                      data-testid="team-invite-copy"
                      className="p-1.5 rounded bg-white border border-[#E6E4DF] hover:bg-[#E8ECEB]"
                    >
                      <Copy className="w-3 h-3"/>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setInviteOpen(false)}>Close</Button>
                <Button onClick={invite} disabled={inviting} data-testid="team-invite-send" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send invite"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Members */}
      <div className="mt-8">
        <div className="label-eyebrow mb-3">Members</div>
        <ul className="divide-y divide-[#E6E4DF] border border-[#E6E4DF] rounded-md">
          {team.members?.map((m) => (
            <li key={m.id} className="p-4 flex items-center justify-between" data-testid={`team-member-${m.id}`}>
              <div>
                <div className="font-medium text-[#1A1A1A] flex items-center gap-2">
                  {m.full_name}
                  {m.id === team.team?.owner_id && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#1C3F35]">
                      <Crown className="w-3 h-3"/>Owner
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5E5E5E] mt-0.5">{m.email}</div>
              </div>
              {m.id !== team.team?.owner_id && (
                <button
                  onClick={()=>removeMember(m.id)}
                  data-testid={`team-remove-${m.id}`}
                  className="text-[#5E5E5E] hover:text-[#C1654B] p-1"
                >
                  <X className="w-4 h-4"/>
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Pending invites */}
      {team.pending_invites?.length > 0 && (
        <div className="mt-6">
          <div className="label-eyebrow mb-3">Pending invites</div>
          <ul className="divide-y divide-[#E6E4DF] border border-[#E6E4DF] rounded-md">
            {team.pending_invites.map((inv) => (
              <li key={inv.id} className="p-4 flex items-center justify-between" data-testid={`team-invite-${inv.id}`}>
                <div>
                  <div className="text-sm text-[#1A1A1A]">{inv.email}</div>
                  <div className="text-xs text-[#5E5E5E] mt-0.5">
                    Invited {new Date(inv.created_at).toLocaleDateString()} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={()=>revokeInvite(inv.id)}
                  data-testid={`team-invite-revoke-${inv.id}`}
                  className="text-[#5E5E5E] hover:text-[#C1654B] p-1"
                >
                  <X className="w-4 h-4"/>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
